import React from "react";
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Typography,
  CircularProgress,
  Chip,
  makeStyles,
} from "@material-ui/core";
import MessagesList from "../MessagesList";
import { ReplyMessageProvider } from "../../context/ReplyingMessage/ReplyingMessageContext";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => ({
  messagesWrapper: {
    display: "flex",
    borderRadius: 8,
    overflow: "hidden",
    border: `1px solid ${theme.palette.divider}`,
    height: 360,
    minHeight: 260,
    "& > div": {
      width: "100%",
      height: "100%",
      minHeight: 0,
    },
  },
  detailLine: {
    marginTop: theme.spacing(1),
  },
}));

const formatDate = (value) => {
  if (!value) return "-";

  let normalizedValue = value;
  if (typeof value === "string") {
    // Suporta datas MySQL sem timezone e com microssegundos.
    normalizedValue = value.includes(" ") ? value.replace(" ", "T") : value;
    normalizedValue = normalizedValue.replace(/\.(\d{3})\d+$/, ".$1");
  }

  const parsed = new Date(normalizedValue);
  if (Number.isNaN(parsed.getTime())) return "-";
  return parsed.toLocaleString();
};

const STATUS_VALUES = ["aguardando", "em_analise", "em_producao", "retornar_cliente", "finalizado"];

const statusLabel = (value) => {
  if (STATUS_VALUES.includes(value)) {
    return i18n.t(`pendingMessages.statuses.${value}`);
  }

  return value || "-";
};

const STATUS_COLORS_HEX = {
  aguardando: "#ff9800",
  em_analise: "#2196f3",
  em_producao: "#00acc1",
  retornar_cliente: "#9c27b0",
  finalizado: "#4caf50",
};

const SolicitacaoDesenvolvimentoDetailsModal = ({ open, onClose, loading, data }) => {
  const classes = useStyles();
  const markedMessageIds = Array.isArray(data?.ultimasMensagens)
    ? data.ultimasMensagens
        .map((message) => String(message?.id || "").trim())
        .filter(Boolean)
    : [];
  const finalizedAtValue = data?.finalizedAt || null;
  const shouldShowFinalizedAt = data?.status === "finalizado";

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>{i18n.t("solicitacoesDesenvolvimento.details.title")}</DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box display="flex" justifyContent="center" py={3}>
            <CircularProgress size={28} />
          </Box>
        ) : !data ? (
          <Typography variant="body2">{i18n.t("solicitacoesDesenvolvimento.details.loadError")}</Typography>
        ) : (
          <>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="subtitle1">
                {i18n.t("solicitacoesDesenvolvimento.cardTitle", { id: data.id, ticketId: data.ticketId || "-" })}
              </Typography>
              <Chip
                label={statusLabel(data.status)}
                size="small"
                style={{
                  backgroundColor: STATUS_COLORS_HEX[data.status] || "#9e9e9e",
                  color: "#fff",
                }}
              />
            </Box>

            <Box className={classes.detailLine}>
              <Typography variant="subtitle2">{i18n.t("solicitacoesDesenvolvimento.fields.contact")}</Typography>
              <Typography variant="body2">{data.contato || i18n.t("common.notInformed")}</Typography>
            </Box>

            <Box className={classes.detailLine}>
              <Typography variant="subtitle2">{i18n.t("common.phone")}</Typography>
              <Typography variant="body2">{data.telefone || i18n.t("common.notInformed")}</Typography>
            </Box>

            <Box className={classes.detailLine}>
              <Typography variant="subtitle2">{i18n.t("solicitacoesDesenvolvimento.details.fullNotes")}</Typography>
              <Typography variant="body2" style={{ whiteSpace: "pre-wrap" }}>
                {data.observacao || i18n.t("solicitacoesDesenvolvimento.noNotes")}
              </Typography>
            </Box>

            <Box className={classes.detailLine}>
              <Typography variant="subtitle2">{i18n.t("solicitacoesDesenvolvimento.details.createdAt")}</Typography>
              <Typography variant="body2">{formatDate(data.createdAt)}</Typography>
            </Box>

            {shouldShowFinalizedAt && (
              <Box className={classes.detailLine}>
                <Typography variant="subtitle2">{i18n.t("solicitacoesDesenvolvimento.details.finalizedAt")}</Typography>
                <Typography variant="body2">{formatDate(finalizedAtValue)}</Typography>
              </Box>
            )}
            

            <Box mt={3} mb={1}>
              <Typography variant="subtitle2">{i18n.t("solicitacoesDesenvolvimento.details.lastMessages")}</Typography>
            </Box>
            <Divider />

            <Box mt={2}>
              {!data?.ticketId ? (
                <Typography variant="body2">{i18n.t("solicitacoesDesenvolvimento.details.noTicket")}</Typography>
              ) : markedMessageIds.length === 0 ? (
                <Typography variant="body2">{i18n.t("solicitacoesDesenvolvimento.details.noMarkedMessages")}</Typography>
              ) : (
                <Box className={classes.messagesWrapper}>
                  <ReplyMessageProvider>
                    <MessagesList
                      ticket={{
                        id: data.ticketId,
                        isGroup: Boolean(data?.isGroup),
                      }}
                      ticketId={data.ticketId}
                      isGroup={Boolean(data?.isGroup)}
                      readOnly
                      allowedMessageIds={markedMessageIds}
                    />
                  </ReplyMessageProvider>
                </Box>
              )}
            </Box>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default SolicitacaoDesenvolvimentoDetailsModal;
